import { Controller } from '@hotwired/stimulus';

/**
 * Org switcher controller — filters a switcher list by a search field, with keyboard navigation,
 * section-aware display and a result counter. Two hosts share it:
 *
 * - « Changer d'organisation » (`shared/_org_switcher_modal`, impersonation banner): rows carry
 *   `data-org-name` / `data-org-siret` (lower-cased by the server) and a `button_to` form. This
 *   path is unchanged: plain `includes` on the lower-cased query, no accent folding.
 * - « Changer de chantier » (`project_switchers/show`, lazy frame of `shared/_project_switcher_modal`):
 *   each row IS a link `<a href data-turbo-frame="_top">` carrying `data-search` (name, n° d'affaire,
 *   client, city, normalised by the server: lower case, no accents). Only on that path is the query
 *   folded the same way (`normalize('NFD')`), so « École » finds « ecole ».
 *
 * Targets: `search` (optional: absent under the search threshold), `list`, `row`, `empty` (optional),
 * `section` (its first `<p>` is the header hidden while searching), `resultCount` (optional).
 * Values: `currentOrgId` (Number), `countNoun` (String, optional): when set, the counter reads
 * « N {noun}(s) » at rest and while searching, and rows sharing an `href` (a project in both
 * « Récents » and « Tous les chantiers ») count once. Without it: « N organisations » / « N résultats ».
 * Actions: `input->org-switcher#filter`, `keydown->org-switcher#onKeydown` (ArrowUp/ArrowDown move the
 * highlight over visible non-current rows; Enter follows a link row or submits an org row's form).
 *
 * @example
 *   <div data-controller="org-switcher" data-org-switcher-count-noun-value="chantier">
 *     <input data-org-switcher-target="search"
 *            data-action="input->org-switcher#filter keydown->org-switcher#onKeydown">
 *     <p data-org-switcher-target="resultCount" aria-live="polite"></p>
 *     <div data-org-switcher-target="section"><p>Récents</p>
 *       <a data-org-switcher-target="row" href="/projects/1" data-turbo-frame="_top"
 *          data-search="ecole jean-moulin aff-2026-12">École Jean-Moulin</a>
 *     </div>
 *     <div class="hidden" data-org-switcher-target="empty">Aucun chantier trouvé</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ['search', 'list', 'row', 'empty', 'section', 'resultCount'];
  static values = { currentOrgId: Number, countNoun: String };

  connect() {
    this.focusedIndex = -1;

    // Autofocus search on modal open via MutationObserver
    this._observeModalVisibility();

    // A lazily loaded body (« Changer de chantier ») connects while its modal is already open: the
    // observer will never see that opening, so run the open routine now. The org switcher is
    // rendered with its modal hidden, so this is a no-op there.
    if (this._modalTarget && !this._modalTarget.classList.contains('hidden')) {
      this._onModalOpen();
    }
  }

  disconnect() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  filter() {
    const rawQuery = this.hasSearchTarget ? this.searchTarget.value.trim() : '';
    const query = rawQuery.toLowerCase();
    const foldedQuery = foldForSearch(rawQuery);
    const isSearching = query.length > 0;
    let visibleCount = 0;

    // Reset keyboard focus on each filter
    this.focusedIndex = -1;
    this._clearHighlight();

    // Show/hide section headers based on search state
    this.sectionTargets.forEach((section) => {
      const header = section.querySelector('p');
      if (header) {
        header.classList.toggle('hidden', isSearching);
      }
    });

    // While searching the section headers are hidden, so a chantier listed in « Récents » AND « Tous les
    // chantiers » (same href) shows once, its first copy: the list agrees with the counter (QA PR 3, Q3.44).
    // Organisation rows carry no href and are never folded.
    const seenHrefs = new Set();
    this.rowTargets.forEach((row) => {
      let matches = !query || this._rowMatches(row, query, foldedQuery);
      const href = row.getAttribute('href');
      if (matches && isSearching && href) {
        if (seenHrefs.has(href)) matches = false;
        else seenHrefs.add(href);
      }

      row.classList.toggle('hidden', !matches);
      if (matches) visibleCount++;
    });

    // Hide empty sections (all rows hidden)
    this.sectionTargets.forEach((section) => {
      const rows = section.querySelectorAll('[data-org-switcher-target="row"]');
      const anyVisible = Array.from(rows).some((r) => !r.classList.contains('hidden'));
      section.classList.toggle('hidden', !anyVisible);
    });

    // Update result count
    if (this.hasResultCountTarget) {
      this._setCount(this._countLabel(isSearching, visibleCount));
    }

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle('hidden', visibleCount > 0);
    }
  }

  onKeydown(event) {
    const items = this._navigableItems();
    if (items.length === 0) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        this.focusedIndex = Math.min(this.focusedIndex + 1, items.length - 1);
        this._applyHighlight(items);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
        this._applyHighlight(items);
        break;
      }
      case 'Enter': {
        event.preventDefault();
        if (this.focusedIndex >= 0 && this.focusedIndex < items.length) {
          const item = items[this.focusedIndex];
          if (item.matches('a[href]')) {
            // Project row: the link itself, so Turbo follows its `data-turbo-frame="_top"`.
            item.click();
          } else {
            const form = item.querySelector('form');
            if (form) form.requestSubmit();
          }
        }
        break;
      }
    }
  }

  // --- Private ---

  /**
   * A row with `data-search` (project switcher) is compared accent-free; any other row keeps the
   * org switcher's original comparison on `data-org-name` / `data-org-siret`.
   */
  _rowMatches(row, query, foldedQuery) {
    const search = row.dataset.search;
    if (search !== undefined) return search.includes(foldedQuery);

    const name = row.dataset.orgName || '';
    const siret = row.dataset.orgSiret || '';
    return name.includes(query) || siret.includes(query);
  }

  _countLabel(isSearching, visibleCount) {
    if (this.hasCountNounValue && this.countNounValue) {
      const rows = isSearching
        ? this.rowTargets.filter((row) => !row.classList.contains('hidden'))
        : this.rowTargets;
      const count = uniqueRowCount(rows);
      return `${count} ${this.countNounValue}${count > 1 ? 's' : ''}`;
    }

    if (isSearching) {
      return visibleCount === 0
        ? '0 résultats'
        : `${visibleCount} résultat${visibleCount > 1 ? 's' : ''}`;
    }
    const total = this.rowTargets.length;
    return `${total} organisation${total > 1 ? 's' : ''}`;
  }

  // Rewriting an identical text node can make a live region announce it again on every keystroke.
  _setCount(text) {
    if (this.resultCountTarget.textContent !== text) {
      this.resultCountTarget.textContent = text;
    }
  }

  _navigableItems() {
    return this.rowTargets.filter(
      (row) => !row.classList.contains('hidden') && row.dataset.orgCurrent !== 'true'
    );
  }

  _applyHighlight(items) {
    this._clearHighlight();
    if (this.focusedIndex >= 0 && this.focusedIndex < items.length) {
      items[this.focusedIndex].classList.add('bg-neutral-100');
      items[this.focusedIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  _clearHighlight() {
    this.rowTargets.forEach((row) => row.classList.remove('bg-neutral-100'));
  }

  _observeModalVisibility() {
    // Walk up to find the modal root (the element with data-controller="modal")
    const modal = this.element.closest('[data-controller="modal"]');
    if (!modal) return;

    const modalTarget = modal.querySelector('[data-modal-target="modal"]') || modal;
    this._modalTarget = modalTarget;

    this._observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isVisible = !modalTarget.classList.contains('hidden');
          if (isVisible) {
            this._onModalOpen();
          }
        }
      }
    });

    this._observer.observe(modalTarget, { attributes: true, attributeFilter: ['class'] });
  }

  _onModalOpen() {
    // Reset state on each open
    if (this.hasSearchTarget) this.searchTarget.value = '';
    this.focusedIndex = -1;
    this._clearHighlight();
    this.filter();

    // Focus search input with a small delay for the transition
    requestAnimationFrame(() => {
      if (this.hasSearchTarget) this.searchTarget.focus();
    });
  }
}

/**
 * Lower case without diacritics, the server's `data-search` normal form (« SAINT-ÉTIENNE » → « saint-etienne »).
 * Same rule as `ProjectSwitchersController#project_switcher_search_text`: NFD, then every \p{Mn} mark removed.
 */
function foldForSearch(text) {
  return text
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase();
}

/** Rows sharing an `href` are one destination (Récents + Tous les chantiers); a row without one counts alone. */
function uniqueRowCount(rows) {
  return new Set(rows.map((row) => row.getAttribute('href') ?? row)).size;
}
